import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, apiErr } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Anchor, Ship, Wrench, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "professional" ? "professional" : "owner";
  const { login, register } = useAuth();

  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [pro, setPro] = useState({ services: [], coverage_mode: "all", coverage_provinces: [], coverage_comarcas: [], coverage_ports: [], bio: "" });
  const [services, setServices] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggle = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  useEffect(() => {
    if (mode === "register") {
      api.get("/services").then((r) => setServices(r.data));
      api.get("/geo/provinces").then((r) => setProvinces(r.data));
    }
  }, [mode]);

  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`¡Hola de nuevo, ${u.name.split(" ")[0]}!`);
        nav(u.role === "admin" ? "/admin" : u.role === "professional" ? "/pro" : "/owner");
      } else {
        const payload = { ...form, role, ...(role === "professional" ? pro : {}) };
        const u = await register(payload);
        toast.success("¡Cuenta creada! Bienvenido a MarinaHub");
        nav(u.role === "professional" ? "/pro" : "/owner");
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const currentProvinces = provinces.filter(p => pro.coverage_provinces.includes(p.name));
  const availableComarcas = currentProvinces.flatMap(p => p.comarcas);
  const availablePorts = currentProvinces.flatMap(p => p.ports.map(pt => typeof pt === "string" ? pt : pt.name));

  return (
    <div className="min-h-screen hub-cream flex">
      <div className="hidden lg:flex lg:w-1/2 hub-navy relative overflow-hidden">
        <img src="https://images.pexels.com/photos/35609615/pexels-photo-35609615.jpeg" className="absolute inset-0 w-full h-full object-cover opacity-40" alt="" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1D3A]/80 to-[#0B1D3A]/20" />
        <div className="relative p-16 flex flex-col justify-between text-white w-full">
          <Link to="/" className="flex items-center gap-2.5 w-fit">
            <div className="w-9 h-9 rounded-full bg-white text-[#0B1D3A] flex items-center justify-center"><Anchor size={18}/></div>
            <div className="font-serif text-2xl">Marina<span className="hub-coral italic">Hub</span></div>
          </Link>
          <div>
            <h2 className="font-serif text-5xl leading-tight">{mode === "login" ? "Bienvenido de vuelta al puerto." : "Únete a la comunidad náutica de España."}</h2>
            <p className="mt-4 text-white/70 max-w-md">{mode === "login" ? "Tu barco te espera. Y también tu próximo trabajo bien hecho." : "Miles de propietarios y profesionales conectan cada mes en MarinaHub."}</p>
          </div>
          <div className="text-white/50 text-xs font-display uppercase tracking-widest">Sin comisiones · Verificados · 100% en español</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-full bg-[#0B1D3A] text-white flex items-center justify-center"><Anchor size={16}/></div>
            <div className="font-serif text-xl">Marina<span className="hub-coral italic">Hub</span></div>
          </Link>
          <div className="hub-pill hub-sand text-[#0B1D3A] mb-4">{mode === "login" ? "Iniciar sesión" : "Crear cuenta"}</div>
          <h1 className="font-serif text-4xl text-[#0B1D3A] tracking-tight">{mode === "login" ? "Entra a tu panel" : "Empieza en 2 minutos"}</h1>

          {mode === "register" && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={() => setRole("owner")} data-testid="role-owner-btn" className={`p-4 rounded-xl border-2 text-left transition-colors ${role === "owner" ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-[#0B1D3A]/15 bg-white"}`}>
                <Ship size={18} />
                <div className="font-display font-semibold mt-2">Propietario</div>
                <div className="text-xs opacity-70">Necesito servicios</div>
              </button>
              <button onClick={() => setRole("professional")} data-testid="role-pro-btn" className={`p-4 rounded-xl border-2 text-left transition-colors ${role === "professional" ? "border-[#FF6B57] bg-[#FF6B57] text-white" : "border-[#0B1D3A]/15 bg-white"}`}>
                <Wrench size={18} />
                <div className="font-display font-semibold mt-2">Profesional</div>
                <div className="text-xs opacity-70">Ofrezco servicios</div>
              </button>
            </div>
          )}

          <div className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest font-display">Nombre completo</Label>
                <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Óscar Martín" className="mt-1.5 rounded-lg border-[#0B1D3A]/15" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-display">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tu@email.com" className="mt-1.5 rounded-lg border-[#0B1D3A]/15" data-testid="auth-email-input"/>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-display">Contraseña</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" className="mt-1.5 rounded-lg border-[#0B1D3A]/15" data-testid="auth-password-input"/>
            </div>
            {mode === "register" && (
              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-widest font-display">Teléfono (opcional)</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" className="mt-1.5 rounded-lg border-[#0B1D3A]/15" data-testid="auth-phone-input"/>
              </div>
            )}

            {mode === "register" && role === "professional" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Servicios que ofreces</Label>
                  <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                    {services.map((s) => {
                      const on = pro.services.includes(s.id);
                      return (
                        <button key={s.id} type="button" data-testid={`svc-${s.id}`}
                          onClick={() => setPro({ ...pro, services: on ? pro.services.filter(x => x !== s.id) : [...pro.services, s.id] })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Cobertura</Label>
                  <p className="text-xs text-[#5C6B7D] mt-1">Cuanto más ajustada, mejor te llegarán solo los trabajos de tu zona.</p>
                  <div className="mt-2 grid grid-cols-4 gap-1 p-1 bg-white border border-[#0B1D3A]/15 rounded-full">
                    {[
                      { k: "all", l: "Toda España" },
                      { k: "provinces", l: "Provincias" },
                      { k: "comarcas", l: "Comarcas" },
                      { k: "ports", l: "Puertos" },
                    ].map(({ k, l }) => (
                      <button key={k} type="button"
                        onClick={() => setPro({ ...pro, coverage_mode: k, coverage_provinces: [], coverage_comarcas: [], coverage_ports: [] })}
                        data-testid={`cov-mode-${k}`}
                        className={`text-xs py-2 rounded-full font-display font-semibold transition-colors ${pro.coverage_mode === k ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {pro.coverage_mode === "all" && (
                  <div className="hub-card p-4 text-sm text-[#5C6B7D] bg-emerald-50 border-emerald-200">
                    ✓ Recibirás solicitudes de <strong className="text-[#0B1D3A]">todos los puertos de España</strong>.
                  </div>
                )}
                {pro.coverage_mode !== "all" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">
                      {pro.coverage_mode === "provinces" ? "Elige provincias" : "1. Elige provincia(s) primero"}
                    </Label>
                    {pro.coverage_mode !== "provinces" && (
                      <p className="text-xs text-[#5C6B7D] mt-1">Después podrás afinar por {pro.coverage_mode === "comarcas" ? "comarca" : "puerto"}.</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                      {provinces.map((p) => {
                        const on = pro.coverage_provinces.includes(p.name);
                        return (
                          <button key={p.name} type="button" data-testid={`prov-${p.name}`}
                            onClick={() => setPro({ ...pro, coverage_provinces: toggle(pro.coverage_provinces, p.name) })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {pro.coverage_mode === "comarcas" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">2. Elige comarcas</Label>
                    {pro.coverage_provinces.length === 0 ? (
                      <div className="mt-2 p-3 rounded-lg border border-dashed border-[#0B1D3A]/20 text-xs text-[#5C6B7D] italic">
                        Selecciona al menos una provincia arriba para ver sus comarcas.
                      </div>
                    ) : availableComarcas.length === 0 ? (
                      <div className="mt-2 p-3 rounded-lg border border-dashed border-[#0B1D3A]/20 text-xs text-[#5C6B7D] italic">
                        Esta provincia no tiene comarcas costeras registradas.
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                        {availableComarcas.map((c) => {
                          const on = pro.coverage_comarcas.includes(c);
                          return (
                            <button key={c} type="button" data-testid={`com-${c}`}
                              onClick={() => setPro({ ...pro, coverage_comarcas: toggle(pro.coverage_comarcas, c) })}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {pro.coverage_mode === "ports" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">2. Elige puertos</Label>
                    {pro.coverage_provinces.length === 0 ? (
                      <div className="mt-2 p-3 rounded-lg border border-dashed border-[#0B1D3A]/20 text-xs text-[#5C6B7D] italic">
                        Selecciona al menos una provincia arriba para ver sus puertos.
                      </div>
                    ) : availablePorts.length === 0 ? (
                      <div className="mt-2 p-3 rounded-lg border border-dashed border-[#0B1D3A]/20 text-xs text-[#5C6B7D] italic">
                        Esta provincia no tiene puertos registrados.
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-3 rounded-lg border border-[#0B1D3A]/15">
                        {availablePorts.map((p) => {
                          const on = pro.coverage_ports.includes(p);
                          return (
                            <button key={p} type="button" data-testid={`prt-${p}`}
                              onClick={() => setPro({ ...pro, coverage_ports: toggle(pro.coverage_ports, p) })}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                              {p}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Sobre ti (opcional)</Label>
                  <Textarea value={pro.bio} onChange={(e) => setPro({ ...pro, bio: e.target.value })} placeholder="Años de experiencia, especialidad, certificados..." className="mt-1.5 rounded-lg border-[#0B1D3A]/15" data-testid="pro-bio-input"/>
                </div>
              </>
            )}

            <Button onClick={submit} disabled={loading} className="hub-btn-primary w-full rounded-full h-11 gap-2 mt-2" data-testid="auth-submit-btn">
              {loading ? "..." : mode === "login" ? "Entrar" : "Crear cuenta"}
              <ArrowRight size={16}/>
            </Button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#0B1D3A]/10"/></div>
              <div className="relative flex justify-center text-xs uppercase tracking-widest"><span className="bg-white px-3 text-[#5C6B7D] font-display">o continúa con</span></div>
            </div>
            <button type="button" onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }} className="w-full h-11 rounded-full border border-[#0B1D3A]/15 flex items-center justify-center gap-3 font-display font-semibold hover:bg-[#FBF9F5] transition-colors" data-testid="google-login-btn">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.5 2.4 30.1 0 24 0 14.8 0 6.9 5.2 3 12.9l7.8 6C12.5 13.1 17.8 9.5 24 9.5z"/><path fill="#4285F4" d="M46.9 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.9c-.6 3-2.3 5.6-4.9 7.3l7.6 5.9c4.4-4.1 6.9-10.1 6.9-17.5z"/><path fill="#FBBC05" d="M10.8 28.9c-.5-1.5-.8-3.1-.8-4.9s.3-3.4.8-4.9l-7.8-6C1.1 16.4 0 20.1 0 24s1.1 7.6 3 10.9l7.8-6z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15.1-5.5l-7.6-5.9c-2.1 1.4-4.7 2.2-7.5 2.2-6.2 0-11.5-3.6-13.2-9.3l-7.8 6C6.9 42.8 14.8 48 24 48z"/></svg>
              Google
            </button>

            <div className="text-center text-sm text-[#5C6B7D] pt-2">
              {mode === "login" ? (
                <>¿No tienes cuenta? <Link to="/register" className="text-[#FF6B57] font-semibold" data-testid="switch-to-register">Regístrate</Link></>
              ) : (
                <>¿Ya tienes cuenta? <Link to="/login" className="text-[#FF6B57] font-semibold" data-testid="switch-to-login">Inicia sesión</Link></>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
